import { useTranslation } from 'react-i18next';
import { useCurrencySymbol } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import type { CardDetailsResponse } from '@shared/api';
import { AccountInfoRow } from './accountInfoRow';
import { CardItem } from '@widgets';
import { Text } from '@shared/widgets';

interface AccountInfoProps {
  className?: string;
  card: CardDetailsResponse;
}

export const AccountInfo = (props: AccountInfoProps) => {
  const { t } = useTranslation('card');

  const symbol = useCurrencySymbol(props.card.currency);
  return (
    <div
      className={cn(
        'flex flex-col gap-3 bg-white p-6 dark:bg-soft-black',
        PaletteUtils.isInsigniaPalette && 'rounded-sm',
        PaletteUtils.isVcfcPalette && 'rounded-lg',
        props.className,
      )}
    >
      <div className='flex justify-between'>
        <div className='relative z-10 flex flex-col gap-2'>
          <Text weight='medium' size='2xl'>
            {props.card.cardLabel}
          </Text>
          {PaletteUtils.isVcfcPalette && <CardItem card={props.card} />}
          {PaletteUtils.isInsigniaPalette && (
            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-2'>
                <Text weight='medium' size='xs' color='gray'>
                  {props.card.productDisplayName}
                </Text>
                <Text weight='medium'>{props.card.cardHolder}</Text>
              </div>
              <Text uppercase weight='medium' size='xs' color='gray'>
                {t('accountInfo.fields.type2')}
              </Text>
            </div>
          )}
        </div>
        {PaletteUtils.isInsigniaPalette && (
          <div
            className={cn(
              'relative flex h-40 w-48 flex-none flex-col items-center justify-end overflow-hidden rounded-tr-sm bg-soft-black pb-4 pl-14 pr-1 pt-7 text-center dark:bg-slate',
              'before:absolute before:-bottom-[2px] before:right-[calc(100%-2px)] before:z-[1] before:h-[150%] before:w-1/2 before:origin-bottom-right before:rotate-[20deg] before:border before:border-r-primary before:bg-white dark:before:bg-soft-black',
            )}
          >
            <div
              className={cn(
                'mb-3',
                'before:text-[72px] before:leading-[1] before:text-primary before:content-[attr(data-icon)]',
                'after:absolute after:-bottom-[52px] after:-left-[6px] after:text-[156px] after:leading-[1] after:text-primary after:opacity-5 after:content-[attr(data-icon)]',
              )}
              data-icon={symbol}
            />
            <Text weight='semibold' color='white'>
              {props.card.status}
            </Text>
          </div>
        )}
      </div>
      <ul className='flex flex-col gap-3'>
        <li className='h-px w-full bg-light/40' />
        <AccountInfoRow label={t('accountInfo.fields.cardNumber')} value={props.card.cardNumber} />
        <AccountInfoRow label={t('accountInfo.fields.accountNumber')} value={props.card.accountNumber} />
      </ul>
    </div>
  );
};
